/**
 * Dropbox storage implementation
 * Infrastructure layer - integrates with external Dropbox API
 */

import { Dropbox, DropboxAuth, files } from 'dropbox';
import { Photo, isVideo } from '../core/domain.js';
import { IPhotoStorage } from '../core/ports.js';

/**
 * Configuration for Dropbox storage
 */
export interface DropboxStorageConfig {
  clientId: string;
  folderPath: string;
}

// Auth storage keys (all in sessionStorage - cleared on browser close)
const CODE_VERIFIER_KEY = 'dropbox_code_verifier';
const REFRESH_TOKEN_KEY = 'dropbox_refresh_token';
const OAUTH_STATE_KEY = 'dropbox_oauth_state';

/**
 * Supported image file extensions
 */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];

/**
 * Supported video file extensions
 */
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v'];

/**
 * Index filename (stored in the photos folder)
 */
const INDEX_FILENAME = '.photo-index.json';

/**
 * Starred photos filename (stored in the photos folder)
 */
const STARRED_FILENAME = '.photo-starred.json';

const EXIF_PARTIAL_READ_SIZE = 128 * 1024;

/**
 * Make JSON values safe for HTTP headers by escaping non-ASCII characters
 * From Dropbox documentation: https://www.dropbox.com/developers/reference/json-encoding
 */
function httpHeaderSafeJson(v: any): string {
  const charsToEncode = /[\u007f-\uffff]/g;
  return JSON.stringify(v).replace(charsToEncode,
    function (c) {
      return '\\u' + ('000' + c.charCodeAt(0).toString(16)).slice(-4);
    }
  );
}

/**
 * Parse EXIF date as UTC timestamp
 * EXIF DateTimeOriginal format: "YYYY:MM:DD HH:MM:SS" (no timezone info)
 * We treat camera time as UTC for consistency across the application
 */
function parseExifDateAsUTC(exifDate: string | Date): number {
  if (exifDate instanceof Date) {
    return exifDate.getTime();
  }

  // Convert EXIF format "YYYY:MM:DD HH:MM:SS" to ISO format "YYYY-MM-DDTHH:MM:SSZ"
  // Example: "2024:12:15 14:30:45" -> "2024-12-15T14:30:45Z"
  const isoDate = exifDate.replace(
    /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
    '$1-$2-$3T$4:$5:$6Z'
  );
  return new Date(isoDate).getTime();
}

/**
 * Per-photo entry in the index (compact format)
 */
interface PhotoIndexEntry {
  p: string;   // path
  d: number;   // dateTaken (ms timestamp)
  m: number;   // fileModified (ms timestamp)
  s: number;   // fileSize (bytes)
  o: number;   // orientation (1-8, EXIF value)
  w: number;   // width (pixels)
  h: number;   // height (pixels)
  e?: boolean; // exifExtracted - true if EXIF extracted, false/undefined if not
}

/**
 * Photo index structure stored in Dropbox
 */
interface PhotoIndex {
  v: number;              // version
  u: number;              // lastUpdated (ms timestamp)
  c?: string;             // Dropbox cursor (for incremental updates)
  ph: PhotoIndexEntry[];  // photos array
}

/**
 * Result from detecting changes using Dropbox cursor
 */
interface ChangeDetectionResult {
  changes: (files.DeletedMetadataReference | files.FileMetadataReference | files.FolderMetadataReference)[];
  cursor: string;
}

/**
 * Starred photos structure stored in Dropbox
 */
interface StarredPhotos {
  v: number;    // version
  u: number;    // lastUpdated (ms timestamp)
  st: string[]; // starred photo paths
}

/**
 * LRU cache for full-size photo object URLs
 */
class FullSizeCache {
  private cache = new Map<string, string>();
  private maxSize: number;

  constructor(maxSize: number = 50) {
    this.maxSize = maxSize;
  }

  get(photoId: string): string | undefined {
    const url = this.cache.get(photoId);
    if (url) {
      // Move to end (most recently used)
      this.cache.delete(photoId);
      this.cache.set(photoId, url);
    }
    return url;
  }

  set(photoId: string, url: string): void {
    // Remove if already exists
    if (this.cache.has(photoId)) {
      this.cache.delete(photoId);
    }

    // Evict oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value as string | undefined;
      if (oldestKey) {
        const oldestUrl = this.cache.get(oldestKey);
        if (oldestUrl) {
          // Revoke object URL to free memory
          URL.revokeObjectURL(oldestUrl);
        }
        this.cache.delete(oldestKey);
      }
    }

    // Add to end (most recently used)
    this.cache.set(photoId, url);
  }
}

/**
 * Rate limiter to prevent hitting Dropbox API rate limits
 * Uses token bucket algorithm with exponential backoff on 429 errors
 */
class RateLimiter {
  private queue: Array<{
    execute: () => Promise<any>;
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];
  private isProcessing = false;
  private backoffDelay = 0; // Current backoff delay in ms
  private readonly minDelay = 100; // Minimum delay between requests
  private readonly maxBackoff = 30000; // Max backoff of 30 seconds
  private readonly backoffMultiplier = 2;

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({ execute: fn, resolve, reject });
      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const item = this.queue.shift()!;

      // Wait for backoff delay if any
      const delay = Math.max(this.minDelay, this.backoffDelay);
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      try {
        const result = await item.execute();
        // Success - reduce backoff
        this.backoffDelay = Math.max(0, this.backoffDelay / 2);
        item.resolve(result);
      } catch (error: any) {
        // Check for rate limit error (429)
        if (error?.status === 429 || error?.message?.includes('429')) {
          // Increase backoff exponentially
          this.backoffDelay = Math.min(
            this.maxBackoff,
            Math.max(1000, this.backoffDelay * this.backoffMultiplier)
          );
          console.warn(`Rate limited, backing off for ${this.backoffDelay}ms`);
          // Re-queue the request to retry
          this.queue.unshift(item);
        } else {
          item.reject(error);
        }
      }
    }

    this.isProcessing = false;
  }

  // Reset backoff (e.g., after successful requests)
  resetBackoff(): void {
    this.backoffDelay = 0;
  }
}

export class DropboxStorage implements IPhotoStorage {
  private dbx: Dropbox;
  private fullSizeCache = new FullSizeCache();
  private folderPath: string;
  private indexFilePath: string;
  private starredFilePath: string;
  private starredPaths: Set<string> = new Set();
  private starredFileRev: string | null = null; // Revision for optimistic concurrency
  private indexFileRev: string | null = null; // Revision for optimistic concurrency
  private needsReload: boolean = false; // Set on conflict during EXIF extraction
  private exifExtractionState: {
    isRunning: boolean;
    completed: number;
    total: number;
  } | null = null;
  private exifr: any = null; // Cache exifr module to avoid repeated dynamic imports
  private cachedCursor: string | undefined; // Cache cursor to avoid reloading index
  private wakeLock: any = null; // Screen wake lock to prevent system sleep during EXIF extraction
  private visibilityAbortController: AbortController | null = null; // To abort extraction if page becomes hidden
  private rateLimiter = new RateLimiter(); // Rate limiter for API calls
  private pendingThumbnails = new Set<string>(); // Track photos with pending thumbnail requests

  /**
   * Create a DropboxStorage instance, handling OAuth PKCE flow
   * Returns null if authentication is needed (caller should show auth prompt)
   */
  static async create(config: DropboxStorageConfig): Promise<DropboxStorage | null> {
    const auth = new DropboxAuth({ clientId: config.clientId });

    // Check for OAuth callback with authorization code
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const returnedState = urlParams.get('state');

    if (authCode) {
      // Verify state parameter for CSRF protection
      const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
      if (!storedState || storedState !== returnedState) {
        console.error('OAuth state mismatch - possible CSRF attack');
        sessionStorage.removeItem(OAUTH_STATE_KEY);
        sessionStorage.removeItem(CODE_VERIFIER_KEY);
        return null;
      }
      sessionStorage.removeItem(OAUTH_STATE_KEY);

      // Exchange authorization code for tokens using PKCE
      const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
      if (!codeVerifier) {
        console.error('No code verifier found - PKCE flow incomplete');
        return null;
      }

      sessionStorage.removeItem(CODE_VERIFIER_KEY);

      try {
        auth.setCodeVerifier(codeVerifier);
        const redirectUri = window.location.origin + window.location.pathname;
        const response = await auth.getAccessTokenFromCode(redirectUri, authCode);

        const result = response.result as any;
        if (result.refresh_token) {
          sessionStorage.setItem(REFRESH_TOKEN_KEY, result.refresh_token);
          auth.setRefreshToken(result.refresh_token);
        }
        if (result.access_token) {
          auth.setAccessToken(result.access_token);
        }

        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);

        const dbx = new Dropbox({ auth });
        return new DropboxStorage(dbx, config.folderPath);
      } catch (error) {
        console.error('Token exchange failed:', error);
        return null;
      }
    }

    // Check for existing refresh token
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      auth.setRefreshToken(refreshToken);
      const dbx = new Dropbox({ auth });
      // SDK will auto-refresh access token on first API call
      return new DropboxStorage(dbx, config.folderPath);
    }

    // No auth - caller should show auth prompt
    return null;
  }

  /**
   * Get the OAuth URL for authentication using PKCE
   * Stores code verifier in sessionStorage for the callback
   */
  static async getAuthUrl(clientId: string): Promise<string> {
    const auth = new DropboxAuth({ clientId });
    const redirectUri = window.location.origin + window.location.pathname;

    // Generate random state for CSRF protection
    const stateArray = new Uint8Array(16);
    crypto.getRandomValues(stateArray);
    const state = Array.from(stateArray, b => b.toString(16).padStart(2, '0')).join('');

    const authUrl = await auth.getAuthenticationUrl(
      redirectUri,
      state,      // state for CSRF protection
      'code',     // authType
      'offline',  // tokenAccessType (offline = refresh token)
      undefined,  // scope
      'none',     // includeGrantedScopes
      true        // usePKCE
    );

    // Store code verifier and state for the callback
    sessionStorage.setItem(CODE_VERIFIER_KEY, auth.getCodeVerifier());
    sessionStorage.setItem(OAUTH_STATE_KEY, state);

    return authUrl as string;
  }

  /**
   * Clear authentication (logout)
   */
  static clearAuth(): void {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(CODE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
  }

  private constructor(dbx: Dropbox, folderPath: string) {
    this.dbx = dbx;
    this.folderPath = folderPath;
    this.indexFilePath = `${folderPath}/${INDEX_FILENAME}`;
    this.starredFilePath = `${folderPath}/${STARRED_FILENAME}`;
  }

  async scanPhotos(onProgress?: (filesFound: number) => void): Promise<Photo[]> {
    try {
      // Load starred list in parallel with index
      const [existingIndex, starredPaths] = await Promise.all([
        this.loadIndexFromDropbox(),
        this.loadStarredFromDropbox(),
      ]);

      this.starredPaths = starredPaths;
      let photoEntries: PhotoIndexEntry[];
      let cursor: string | undefined;
      let needsSave = false;

      // Check if we need to migrate from v1 to v2 (to add videos to existing photo-only index)
      if (existingIndex && (!existingIndex.v || existingIndex.v === 1)) {
        console.log('Detected v1 index, performing migration scan to add videos...');
        const result = await this.performMigrationScan(existingIndex, onProgress);
        photoEntries = result.photoEntries;
        cursor = result.cursor;
        needsSave = true; // Always save after migration
      } else if (existingIndex && existingIndex.c) {
        // Try incremental update using cursor
        console.log('Found existing index with cursor, checking for changes...');

        try {
          const { changes, cursor: newCursor } = await this.detectChanges(existingIndex.c);
          console.log(`Detected ${changes.length} changes since last scan`);

          // Apply changes to existing index
          photoEntries = this.applyChangesToIndex(existingIndex, changes);
          cursor = newCursor;

          // Check if photo entries actually changed (filter out non-photo changes like index file itself)
          needsSave = this.hasPhotoIndexChanged(existingIndex.ph, photoEntries);

          if (onProgress) {
            onProgress(photoEntries.length);
          }
        } catch (error: any) {
          // If cursor expired, fall back to full scan
          if (error.message === 'CURSOR_EXPIRED') {
            console.log('Cursor expired, performing full scan...');
            const result = await this.performFullScan(onProgress);
            photoEntries = result.photoEntries;
            cursor = result.cursor;
            needsSave = true; // Full scan always needs save
          } else {
            throw error;
          }
        }
      } else {
        // No index or no cursor, do full scan
        console.log('No existing index found, performing full scan...');
        const result = await this.performFullScan(onProgress);
        photoEntries = result.photoEntries;
        cursor = result.cursor;
        needsSave = true; // No existing index, need to save
      }

      // Cache cursor for later use
      this.cachedCursor = cursor;

      // Save updated index only if photo entries changed
      if (needsSave) {
        const updatedIndex: PhotoIndex = {
          v: 2,  // Version 2 supports videos
          u: Date.now(),
          c: cursor,
          ph: photoEntries,
        };
        await this.saveIndexToDropbox(updatedIndex);
      } else {
        console.log('No photo changes detected, skipping index upload');
      }

      // Start background EXIF extraction (don't await - runs in background)
      this.startBackgroundExifExtraction(photoEntries);

      // Convert index entries to Photo objects
      const photos = photoEntries.map(entry => this.indexEntryToPhoto(entry));

      return photos;
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  /**
   * Perform a full scan of the folder
   * Returns photo entries and cursor for future incremental updates
   */
  private async performFullScan(
    onProgress?: (filesFound: number) => void
  ): Promise<{ photoEntries: PhotoIndexEntry[]; cursor: string }> {
    const allFiles: files.FileMetadataReference[] = [];
    let hasMore = true;
    let cursor: string | undefined;

    // List all files, handling pagination
    while (hasMore) {
      const response = cursor
        ? await this.dbx.filesListFolderContinue({ cursor })
        : await this.dbx.filesListFolder({
          path: this.folderPath,
          recursive: true,
        });

      // Filter for files only (not folders)
      const fileEntries = response.result.entries.filter(
        (entry): entry is files.FileMetadataReference =>
          entry['.tag'] === 'file'
      );

      allFiles.push(...fileEntries);

      // Report progress
      if (onProgress) {
        onProgress(allFiles.length);
      }

      hasMore = response.result.has_more;
      cursor = response.result.cursor;
    }

    // Filter for media files (images and videos) and convert to index entries
    const mediaFiles = allFiles.filter((file) => this.isMediaFile(file.name));
    const photoEntries = mediaFiles.map(file => this.fileToIndexEntry(file));

    return {
      photoEntries,
      cursor: cursor || '',
    };
  }

  /**
   * Perform a migration scan from v1 to v2 index
   * Does a full folder scan but preserves existing entries (with EXIF data)
   * Only adds new entries (e.g., videos that were previously filtered out)
   */
  private async performMigrationScan(
    existingIndex: PhotoIndex,
    onProgress?: (filesFound: number) => void
  ): Promise<{ photoEntries: PhotoIndexEntry[]; cursor: string }> {
    // First, do a full scan to find all media files (including videos)
    const allFiles: files.FileMetadataReference[] = [];
    let hasMore = true;
    let cursor: string | undefined;

    console.log('Scanning folder for all media files (photos + videos)...');

    // List all files, handling pagination
    while (hasMore) {
      const response = cursor
        ? await this.dbx.filesListFolderContinue({ cursor })
        : await this.dbx.filesListFolder({
          path: this.folderPath,
          recursive: true,
        });

      // Filter for files only (not folders)
      const fileEntries = response.result.entries.filter(
        (entry): entry is files.FileMetadataReference =>
          entry['.tag'] === 'file'
      );

      allFiles.push(...fileEntries);

      // Report progress
      if (onProgress) {
        onProgress(allFiles.length);
      }

      hasMore = response.result.has_more;
      cursor = response.result.cursor;
    }

    // Create a map of existing entries by path (preserves EXIF data)
    const existingEntriesMap = new Map<string, PhotoIndexEntry>();
    for (const entry of existingIndex.ph) {
      existingEntriesMap.set(entry.p, entry);
    }

    console.log(`Found ${existingEntriesMap.size} existing entries to preserve`);

    // Filter for media files and merge with existing entries
    const mediaFiles = allFiles.filter((file) => this.isMediaFile(file.name));
    const mergedEntries: PhotoIndexEntry[] = [];

    for (const file of mediaFiles) {
      const path = file.path_display || file.path_lower || '';

      if (existingEntriesMap.has(path)) {
        // Keep existing entry (preserves EXIF data)
        mergedEntries.push(existingEntriesMap.get(path)!);
      } else {
        // Add new entry (likely a video that was previously filtered out)
        mergedEntries.push(this.fileToIndexEntry(file));
      }
    }

    const newEntryCount = mergedEntries.length - existingEntriesMap.size;
    console.log(`Migration complete: preserved ${existingEntriesMap.size} existing entries, added ${newEntryCount} new entries`);

    return {
      photoEntries: mergedEntries,
      cursor: cursor || '',
    };
  }

  async fetchThumbnails(photos: Photo[]): Promise<Map<string, string>> {
    try {
      const thumbnailMap = new Map<string, string>();

      // Filter out photos that already have pending requests (deduplication)
      const photosToFetch = photos.filter(p => !this.pendingThumbnails.has(p.id));
      if (photosToFetch.length === 0) {
        return thumbnailMap;
      }

      // Mark these photos as pending
      photosToFetch.forEach(p => this.pendingThumbnails.add(p.id));

      try {
        // Fetch in batches of 25 (Dropbox API limit)
        // Process batches sequentially through rate limiter to avoid flooding
        const batchSize = 25;
        for (let i = 0; i < photosToFetch.length; i += batchSize) {
          const batch = photosToFetch.slice(i, i + batchSize);
          const batchThumbnails = await this.rateLimiter.execute(() =>
            this.fetchThumbnailBatch(batch)
          );
          batchThumbnails.forEach((url, id) => thumbnailMap.set(id, url));
        }

        return thumbnailMap;
      } finally {
        // Clear pending status for these photos
        photosToFetch.forEach(p => this.pendingThumbnails.delete(p.id));
      }
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  async getFullSizeUrl(photo: Photo): Promise<string> {
    try {
      // Branch based on media type
      if (isVideo(photo)) {
        return await this.getVideoStreamingUrl(photo);
      } else {
        return await this.getPhotoObjectUrl(photo);
      }
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  /**
   * Get streaming URL for videos using Dropbox temporary links
   * Returns HTTPS URL that supports HTTP Range requests (streaming)
   * Link expires in 4 hours (no caching needed - browser handles it)
   */
  private async getVideoStreamingUrl(video: Photo): Promise<string> {
    const response = await this.dbx.filesGetTemporaryLink({
      path: video.filePath
    });
    return response.result.link;
  }

  /**
   * Get object URL for photos (cached in memory)
   * Downloads full image and creates blob URL
   */
  private async getPhotoObjectUrl(photo: Photo): Promise<string> {
    // Check cache first
    const cachedUrl = this.fullSizeCache.get(photo.id);
    if (cachedUrl) {
      return cachedUrl;
    }

    // Download full-size image
    const response = await this.dbx.filesDownload({ path: photo.filePath });
    const fileBlob = (response.result as any).fileBlob;

    // Create object URL
    const objectUrl = URL.createObjectURL(fileBlob);

    // Cache it
    this.fullSizeCache.set(photo.id, objectUrl);

    return objectUrl;
  }

  async starPhoto(photo: Photo): Promise<void> {
    try {
      await this.saveStarredWithRetry({ type: 'add', path: photo.filePath });
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  async unstarPhoto(photo: Photo): Promise<void> {
    try {
      await this.saveStarredWithRetry({ type: 'remove', path: photo.filePath });
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  /**
   * Fetch thumbnails for a batch of photos
   * Returns map of photo ID to thumbnail data URL
   */
  private async fetchThumbnailBatch(
    photos: Photo[]
  ): Promise<Map<string, string>> {
    const thumbnailMap = new Map<string, string>();

    // Request thumbnails from Dropbox
    const thumbnailEntries = photos.map((photo) => ({
      path: photo.filePath,
      format: { '.tag': 'jpeg' as const },
      size: { '.tag': 'w256h256' as const },
    }));

    const thumbnailResponse = await this.dbx.filesGetThumbnailBatch({
      entries: thumbnailEntries,
    });

    // Process results
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      const thumbnailResult = thumbnailResponse.result.entries[i];

      if (thumbnailResult['.tag'] === 'success') {
        const thumbnailData = thumbnailResult.thumbnail as any;

        // Dropbox SDK returns base64 encoded string in browser
        if (typeof thumbnailData === 'string') {
          const dataUrl = `data:image/jpeg;base64,${thumbnailData}`;
          thumbnailMap.set(photo.id, dataUrl);
        } else {
          console.warn(`Unexpected thumbnail format for ${photo.filePath}`);
        }
      } else {
        console.warn(`Failed to get thumbnail for ${photo.filePath}`);
      }
    }

    return thumbnailMap;
  }

  /**
   * Check if filename is an image based on extension
   */
  private isImageFile(filename: string): boolean {
    const ext = filename.toLowerCase().split('.').pop();
    return ext ? IMAGE_EXTENSIONS.includes(ext) : false;
  }

  /**
   * Check if a file is a video based on its extension
   */
  private isVideoFile(filename: string): boolean {
    const ext = filename.toLowerCase().split('.').pop();
    return ext ? VIDEO_EXTENSIONS.includes(ext) : false;
  }

  /**
   * Check if a file is a supported media file (image or video)
   */
  private isMediaFile(filename: string): boolean {
    return this.isImageFile(filename) || this.isVideoFile(filename);
  }

  /**
   * Get MIME type from filename extension (supports both images and videos)
   */
  private getMimeType(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop();
    const mimeTypes: Record<string, string> = {
      // Image types
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      heic: 'image/heic',
      heif: 'image/heif',
      // Video types
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      webm: 'video/webm',
      mkv: 'video/x-matroska',
      avi: 'video/x-msvideo',
      m4v: 'video/x-m4v',
    };
    return mimeTypes[ext || ''] || 'image/jpeg';
  }

  /**
   * Load the photo index from Dropbox
   * Returns null if the index doesn't exist or is invalid
   */
  private async loadIndexFromDropbox(): Promise<PhotoIndex | null> {
    try {
      const response = await this.dbx.filesDownload({ path: this.indexFilePath });
      // Capture revision for optimistic concurrency
      this.indexFileRev = response.result.rev;

      const fileBlob = (response.result as any).fileBlob;
      const text = await fileBlob.text();
      const index = JSON.parse(text) as PhotoIndex;

      // Basic validation
      if (!index.v || !Array.isArray(index.ph)) {
        console.warn('Invalid index structure, will rebuild');
        return null;
      }

      return index;
    } catch (error: any) {
      // If file doesn't exist (409 not_found), return null
      if (error?.status === 409 || error?.error?.error?.['.tag'] === 'path' || error?.error?.error?.path?.['.tag'] === 'not_found') {
        console.log('No existing index found, will create new one');
        this.indexFileRev = null; // No file yet
        return null;
      }

      // For other errors, log and return null
      console.warn('Error loading index:', error);
      return null;
    }
  }

  /**
   * Save the photo index to Dropbox
   * Uses conditional write mode for optimistic concurrency
   * On conflict, sets needsReload flag and throws
   */
  private async saveIndexToDropbox(index: PhotoIndex): Promise<void> {
    const json = JSON.stringify(index);
    const blob = new Blob([json], { type: 'application/json' });

    // Use 'update' mode with revision if we have one, otherwise 'add' for first write
    const mode: files.WriteMode = this.indexFileRev
      ? { '.tag': 'update', update: this.indexFileRev }
      : { '.tag': 'add' };

    try {
      const response = await this.dbx.filesUpload({
        path: this.indexFilePath,
        contents: blob,
        mode,
      });

      // Update revision after successful write
      this.indexFileRev = response.result.rev;
      console.log(`Index saved: ${index.ph.length} photos, ${(json.length / 1024).toFixed(1)}KB`);
    } catch (error: any) {
      if (this.isConflictError(error)) {
        console.warn('Index conflict detected - another device/tab modified the index');
        this.needsReload = true;
        throw new Error('Index was modified by another device. Please reload the page.');
      }
      throw error;
    }
  }

  /**
   * Detect changes using Dropbox cursor
   * Returns changed files and new cursor
   * Throws error if cursor is expired (caller should do full scan)
   */
  private async detectChanges(cursor: string): Promise<ChangeDetectionResult> {
    const allChanges: (files.DeletedMetadataReference | files.FileMetadataReference | files.FolderMetadataReference)[] = [];
    let hasMore = true;
    let currentCursor = cursor;

    try {
      while (hasMore) {
        const response = await this.dbx.filesListFolderContinue({ cursor: currentCursor });
        allChanges.push(...response.result.entries);
        hasMore = response.result.has_more;
        currentCursor = response.result.cursor;
      }

      return {
        changes: allChanges,
        cursor: currentCursor,
      };
    } catch (error: any) {
      // If cursor is expired (409 reset), throw so caller can do full scan
      if (error?.status === 409 || error?.error?.error?.['.tag'] === 'reset') {
        console.log('Cursor expired, will do full scan');
        throw new Error('CURSOR_EXPIRED');
      }

      throw error;
    }
  }

  /**
   * Convert PhotoIndexEntry to Photo object
   */
  private indexEntryToPhoto(entry: PhotoIndexEntry): Photo {
    return {
      id: entry.p,
      filePath: entry.p,
      dateTaken: new Date(entry.d),
      fileSize: entry.s,
      mimeType: this.getMimeType(entry.p),
      isStarred: this.starredPaths.has(entry.p),
      mediaType: this.isVideoFile(entry.p) ? 'video' : 'photo',
      // thumbnailUrl will be populated later
    };
  }

  /**
   * Convert Dropbox file metadata to PhotoIndexEntry
   */
  private fileToIndexEntry(file: files.FileMetadataReference): PhotoIndexEntry {
    const dateTaken = file.client_modified
      ? new Date(file.client_modified).getTime()
      : Date.now();

    return {
      p: file.path_display || file.path_lower || '',
      d: dateTaken,
      m: dateTaken,  // Use same as dateTaken until EXIF extraction
      s: file.size,
      o: 1,  // Default orientation until EXIF extraction
      w: 0,  // Unknown until EXIF extraction
      h: 0,  // Unknown until EXIF extraction
    };
  }

  /**
   * Apply incremental changes to an existing index
   * Returns updated photo entries
   */
  private applyChangesToIndex(
    index: PhotoIndex,
    changes: (files.DeletedMetadataReference | files.FileMetadataReference | files.FolderMetadataReference)[]
  ): PhotoIndexEntry[] {
    // Create a map for fast lookup by path
    const photoMap = new Map<string, PhotoIndexEntry>();
    index.ph.forEach(entry => photoMap.set(entry.p, entry));

    // Apply each change
    for (const change of changes) {
      const path = change.path_display || change.path_lower || '';

      if (change['.tag'] === 'deleted') {
        // Remove deleted files
        photoMap.delete(path);
      } else if (change['.tag'] === 'file') {
        // Add or update files (only if they're media files)
        if (this.isMediaFile(change.name)) {
          photoMap.set(path, this.fileToIndexEntry(change));
        }
      }
      // Ignore folders
    }

    return Array.from(photoMap.values());
  }

  /**
   * Check if photo index has changed by comparing entries
   * Returns true if entries differ (photos added/removed/modified)
   */
  private hasPhotoIndexChanged(oldEntries: PhotoIndexEntry[], newEntries: PhotoIndexEntry[]): boolean {
    // Quick check: different count means changed
    if (oldEntries.length !== newEntries.length) {
      console.log(`Photo count changed: ${oldEntries.length} -> ${newEntries.length}`);
      return true;
    }

    // Create maps for comparison (path -> entry)
    const oldMap = new Map(oldEntries.map(e => [e.p, e]));
    const newMap = new Map(newEntries.map(e => [e.p, e]));

    // Check if any paths differ
    for (const path of newMap.keys()) {
      if (!oldMap.has(path)) {
        console.log(`Photo added: ${path}`);
        return true;
      }
    }

    for (const path of oldMap.keys()) {
      if (!newMap.has(path)) {
        console.log(`Photo removed: ${path}`);
        return true;
      }
    }

    // Check if any entries have different metadata
    for (const [path, newEntry] of newMap.entries()) {
      const oldEntry = oldMap.get(path)!;

      // Compare relevant fields (skip path since we already checked it)
      if (
        oldEntry.d !== newEntry.d ||  // dateTaken
        oldEntry.m !== newEntry.m ||  // fileModified
        oldEntry.s !== newEntry.s ||  // fileSize
        oldEntry.o !== newEntry.o ||  // orientation
        oldEntry.w !== newEntry.w ||  // width
        oldEntry.h !== newEntry.h ||  // height
        oldEntry.e !== newEntry.e     // exifExtracted
      ) {
        return true;
      }
    }

    // No changes detected
    return false;
  }

  /**
   * Load the starred photos list from Dropbox
   * Returns empty set if the file doesn't exist or is invalid
   */
  private async loadStarredFromDropbox(): Promise<Set<string>> {
    try {
      const response = await this.dbx.filesDownload({ path: this.starredFilePath });
      // Capture revision for optimistic concurrency
      this.starredFileRev = response.result.rev;

      const fileBlob = (response.result as any).fileBlob;
      const text = await fileBlob.text();
      const starred = JSON.parse(text) as StarredPhotos;

      // Basic validation
      if (!starred.v || !Array.isArray(starred.st)) {
        console.warn('Invalid starred structure, will create new one');
        return new Set();
      }

      return new Set(starred.st);
    } catch (error: any) {
      // If file doesn't exist (409 not_found), return empty set
      if (error?.status === 409 || error?.error?.error?.['.tag'] === 'path' || error?.error?.error?.path?.['.tag'] === 'not_found') {
        console.log('No existing starred list found, will create new one');
        this.starredFileRev = null; // No file yet
        return new Set();
      }

      // For other errors, log and return empty set
      console.warn('Error loading starred list:', error);
      return new Set();
    }
  }

  private readonly MAX_STARRED_RETRY_ATTEMPTS = 5;

  /**
   * Check if an error indicates a write conflict (file was modified by another client)
   */
  private isConflictError(error: any): boolean {
    return (
      error?.status === 409 ||
      error?.error?.error?.['.tag'] === 'path' ||
      error?.error?.error?.reason?.['.tag'] === 'conflict'
    );
  }

  /**
   * Attempt to save the starred list with conditional write mode
   * Throws on conflict for retry handling
   */
  private async attemptSaveStarred(): Promise<void> {
    const starred: StarredPhotos = {
      v: 1,
      u: Date.now(),
      st: Array.from(this.starredPaths),
    };

    const json = JSON.stringify(starred);
    const blob = new Blob([json], { type: 'application/json' });

    // Use 'update' mode with revision if we have one, otherwise 'add' for first write
    const mode: files.WriteMode = this.starredFileRev
      ? { '.tag': 'update', update: this.starredFileRev }
      : { '.tag': 'add' };

    const response = await this.dbx.filesUpload({
      path: this.starredFilePath,
      contents: blob,
      mode,
    });

    // Update revision after successful write
    this.starredFileRev = response.result.rev;
    console.log(`Starred list saved: ${starred.st.length} photos`);
  }

  /**
   * Save starred list with retry on conflict
   * Applies the pending operation, saves, and handles conflicts by reloading and retrying
   * Rolls back in-memory state on non-recoverable failure
   */
  private async saveStarredWithRetry(pendingOp: {
    type: 'add' | 'remove';
    path: string;
  }): Promise<void> {
    // Apply the pending operation
    if (pendingOp.type === 'add') {
      this.starredPaths.add(pendingOp.path);
    } else {
      this.starredPaths.delete(pendingOp.path);
    }

    for (let attempt = 0; attempt < this.MAX_STARRED_RETRY_ATTEMPTS; attempt++) {
      try {
        await this.attemptSaveStarred();
        return; // Success
      } catch (error: any) {
        if (this.isConflictError(error)) {
          console.warn(
            `Starred file conflict (attempt ${attempt + 1}/${this.MAX_STARRED_RETRY_ATTEMPTS}), reloading and retrying...`
          );

          // Reload the file to get latest state and revision
          const latestStarred = await this.loadStarredFromDropbox();

          // Update in-memory state to latest
          this.starredPaths = latestStarred;

          // Re-apply the pending operation
          if (pendingOp.type === 'add') {
            this.starredPaths.add(pendingOp.path);
          } else {
            this.starredPaths.delete(pendingOp.path);
          }

          // Continue to next attempt
          continue;
        }

        // Non-conflict error - rollback and throw
        if (pendingOp.type === 'add') {
          this.starredPaths.delete(pendingOp.path);
        } else {
          this.starredPaths.add(pendingOp.path);
        }
        throw error;
      }
    }

    // Max retries exceeded - rollback
    if (pendingOp.type === 'add') {
      this.starredPaths.delete(pendingOp.path);
    } else {
      this.starredPaths.add(pendingOp.path);
    }
    throw new Error(
      `Failed to save starred file after ${this.MAX_STARRED_RETRY_ATTEMPTS} attempts due to concurrent modifications. Please refresh the page.`
    );
  }

  /**
   * Get EXIF extraction progress
   */
  getExifExtractionProgress(): { completed: number; total: number; isRunning: boolean } | null {
    if (!this.exifExtractionState) return null;
    return {
      completed: this.exifExtractionState.completed,
      total: this.exifExtractionState.total,
      isRunning: this.exifExtractionState.isRunning,
    };
  }

  /**
   * Check if a reload is needed due to a write conflict
   * This happens when another device/tab modified the index during EXIF extraction
   */
  needsReloadFromConflict(): boolean {
    return this.needsReload;
  }

  /**
   * Start background EXIF extraction for photos missing EXIF data
   * Runs asynchronously without blocking
   */
  private async startBackgroundExifExtraction(photoEntries: PhotoIndexEntry[]): Promise<void> {
    // Don't start a new extraction if one is already running
    if (this.exifExtractionState && this.exifExtractionState.isRunning) {
      console.log('EXIF extraction already in progress, skipping new start');
      return;
    }

    // Find photos that need EXIF extraction (e is false or undefined)
    const photosNeedingExif = photoEntries.filter(entry => !entry.e);

    if (photosNeedingExif.length === 0) {
      console.log('All photos already have EXIF metadata');
      return;
    }

    const alreadyExtracted = photoEntries.length - photosNeedingExif.length;
    if (alreadyExtracted > 0) {
      console.log(`Resuming EXIF extraction: ${alreadyExtracted} already completed, ${photosNeedingExif.length} remaining`);
    } else {
      console.log(`Starting EXIF extraction for ${photosNeedingExif.length} photos`);
    }

    console.log('💡 Keep this tab visible and active to prevent browser throttling');
    console.log('💡 System will not sleep during extraction (Wake Lock API)');

    this.exifExtractionState = {
      isRunning: true,
      completed: 0,
      total: photosNeedingExif.length,
    };

    // Acquire wake lock to prevent system sleep
    await this.acquireWakeLock();

    // Setup visibility change detection
    this.setupVisibilityDetection();

    try {
      await this.extractExifInBatches(photosNeedingExif, photoEntries);
      console.log('EXIF extraction complete');
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.warn('EXIF extraction aborted due to page becoming hidden');
      } else {
        console.error('Error during EXIF extraction:', error);
      }
    } finally {
      this.exifExtractionState = null;
      await this.releaseWakeLock();
      this.cleanupVisibilityDetection();
    }
  }

  /**
   * Acquire wake lock to prevent system sleep during EXIF extraction
   */
  private async acquireWakeLock(): Promise<void> {
    try {
      // Check if Wake Lock API is supported
      if ('wakeLock' in navigator) {
        this.wakeLock = await (navigator as any).wakeLock.request('screen');
        console.log('Wake lock acquired - system will not sleep during EXIF extraction');

        // Re-acquire wake lock if it's released (e.g., when user switches tabs and back)
        this.wakeLock.addEventListener('release', () => {
          console.log('Wake lock released');
        });
      } else {
        console.warn('Wake Lock API not supported - system may sleep during extraction');
      }
    } catch (error) {
      console.warn('Failed to acquire wake lock:', error);
    }
  }

  /**
   * Release wake lock
   */
  private async releaseWakeLock(): Promise<void> {
    try {
      if (this.wakeLock) {
        await this.wakeLock.release();
        this.wakeLock = null;
        console.log('Wake lock released');
      }
    } catch (error) {
      console.warn('Failed to release wake lock:', error);
    }
  }

  /**
   * Setup visibility detection to warn/abort if page becomes hidden
   */
  private setupVisibilityDetection(): void {
    this.visibilityAbortController = new AbortController();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.warn('⚠️ Page hidden - EXIF extraction may be throttled or paused by browser');
        console.warn('Keep this tab visible and active to ensure extraction completes');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, {
      signal: this.visibilityAbortController.signal,
    });
  }

  /**
   * Cleanup visibility detection
   */
  private cleanupVisibilityDetection(): void {
    if (this.visibilityAbortController) {
      this.visibilityAbortController.abort();
      this.visibilityAbortController = null;
    }
  }

  /**
   * Extract EXIF in batches, updating index incrementally
   */
  private async extractExifInBatches(
    photosNeedingExif: PhotoIndexEntry[],
    allPhotoEntries: PhotoIndexEntry[]
  ): Promise<void> {
    const BATCH_SIZE = 10; // Process 10 photos at a time
    const SAVE_INTERVAL = 500; // Save index every 100 photos

    // Import exifr once for all photos (not 11,100 times!)
    if (!this.exifr) {
      this.exifr = await import('exifr');
    }

    let completedCount = 0;

    for (let i = 0; i < photosNeedingExif.length; i += BATCH_SIZE) {
      // Check if page is hidden - warn but continue (browser will throttle if needed)
      if (document.hidden) {
        console.warn('⚠️ Page is hidden - extraction may be very slow. Please keep tab visible.');
      }

      const batch = photosNeedingExif.slice(i, i + BATCH_SIZE);

      // Process batch in parallel
      await Promise.all(
        batch.map(entry => this.extractAndUpdateExif(entry, allPhotoEntries))
      );

      completedCount += batch.length;

      // Update progress
      if (this.exifExtractionState) {
        this.exifExtractionState.completed = completedCount;
      }

      // Save index periodically
      if (completedCount % SAVE_INTERVAL === 0 || i + BATCH_SIZE >= photosNeedingExif.length) {
        console.log(`Saving index after extracting ${completedCount}/${photosNeedingExif.length} photos`);
        try {
          await this.saveCurrentIndex(allPhotoEntries);
        } catch (error: any) {
          if (this.needsReload) {
            // Conflict detected - stop extraction gracefully
            console.warn('Stopping EXIF extraction due to index conflict');
            if (this.exifExtractionState) {
              this.exifExtractionState.isRunning = false;
            }
            await this.releaseWakeLock();
            this.cleanupVisibilityDetection();
            return; // Exit extraction loop
          }
          throw error;
        }
      }

      // Add small delay between batches to allow garbage collection and prevent browser lockup
      await new Promise(resolve => setTimeout(resolve, 10));
    }
  }

  /**
   * Extract EXIF for a single photo and update its entry in the index
   */
  private async extractAndUpdateExif(
    entry: PhotoIndexEntry,
    allPhotoEntries: PhotoIndexEntry[]
  ): Promise<void> {
    try {
      // Only process image files (JPEG primarily)
      const ext = entry.p.toLowerCase().split('.').pop() || '';
      if (!['jpg', 'jpeg'].includes(ext)) {
        // For non-JPEG files, set defaults and mark as processed
        entry.o = 1;
        entry.w = 0;
        entry.h = 0;
        entry.e = true; // Mark as extracted (attempted)
        // dateTaken stays as fileModified
        return;
      }

      // Fetch partial file (just the beginning for EXIF headers)
      const fileData = await this.fetchPartialFile(entry.p, EXIF_PARTIAL_READ_SIZE);

      // Parse EXIF using exifr
      const exif = await this.parseExif(fileData);

      if (exif) {
        // Update entry with EXIF data
        if (exif.DateTimeOriginal) {
          entry.d = parseExifDateAsUTC(exif.DateTimeOriginal);
        }
        entry.o = exif.Orientation || 1;
        entry.w = exif.ImageWidth || exif.ExifImageWidth || exif.PixelXDimension || 0;
        entry.h = exif.ImageHeight || exif.ExifImageHeight || exif.PixelYDimension || 0;
        entry.e = true; // Mark as extracted
      } else {
        // No EXIF found, use defaults
        entry.o = 1;
        entry.w = 0;
        entry.h = 0;
        entry.e = true; // Mark as extracted (has no EXIF data)
      }
    } catch (error) {
      console.error(`Failed to extract EXIF for ${entry.p}:`, error);
      // Set defaults on error, but do not mark as extracted so it will be attempted again
      entry.o = 1;
      entry.w = 0;
      entry.h = 0;
      entry.e = false;
    }
  }

  /**
   * Fetch partial file from Dropbox using Range requests
   * Gets only the first maxBytes (typically 64KB for EXIF headers)
   */
  private async fetchPartialFile(path: string, maxBytes: number): Promise<ArrayBuffer> {
    try {
      // Get access token from the Dropbox instance
      const accessToken = (this.dbx as any).auth.accessToken;

      // Make direct API call to Dropbox with Range header
      const response = await fetch('https://content.dropboxapi.com/2/files/download', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Dropbox-API-Arg': httpHeaderSafeJson({ path: path }),
          'Range': `bytes=0-${maxBytes - 1}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      return arrayBuffer;
    } catch (error: any) {
      this.handleDropboxError(error);
      throw error;
    }
  }

  /**
   * Parse EXIF data from file buffer using exifr
   */
  private async parseExif(buffer: ArrayBuffer): Promise<any> {
    try {
      // Use cached exifr module (imported once in extractExifInBatches)
      if (!this.exifr) {
        this.exifr = await import('exifr');
      }

      // Parse with exifr - translateKeys defaults to true to get field names
      const exifData = await this.exifr.parse(buffer, {
        pick: ['DateTimeOriginal', 'Orientation', 'ImageWidth', 'ImageHeight', 'ExifImageWidth', 'ExifImageHeight', 'PixelXDimension', 'PixelYDimension'],
        translateValues: false,
      });
      return exifData;
    } catch (error) {
      // EXIF parsing failed - not all JPEGs have EXIF
      return null;
    }
  }

  /**
   * Save current index to Dropbox (preserves cursor)
   */
  private async saveCurrentIndex(photoEntries: PhotoIndexEntry[]): Promise<void> {
    // Use cached cursor instead of reloading entire index
    const updatedIndex: PhotoIndex = {
      v: 2,  // Version 2 supports videos
      u: Date.now(),
      c: this.cachedCursor, // Use cached cursor
      ph: photoEntries,
    };

    await this.saveIndexToDropbox(updatedIndex);
  }

  /**
   * Handle Dropbox API errors
   * If token is expired (401), clear it and reload to trigger re-auth
   */
  private handleDropboxError(error: any): void {
    // Check if it's a 401 Unauthorized error
    if (error?.status === 401 || error?.error?.error?.['.tag'] === 'invalid_access_token') {
      console.warn('Dropbox access token expired or invalid, clearing and re-authenticating...');

      // Clear auth and reload to trigger auth flow
      DropboxStorage.clearAuth();
      window.location.reload();
    }
  }
}
